import store from '@/store';
import api, { toGraphQLQuery, getGraphQLHeaders } from '@/api';
import log from '@/configured/logger';
import constants from '@/constants';
import { localeToCraftSiteHandle } from '@/configured/languages/config';
import {
  isBootstrapRequest,
  normalizeBootstrapOptions,
  runSharedLoad,
} from '@/base-data/shared-load';

const exports = {
  namespace: 'favorites',
  module: {
    namespaced: true,
    state: {
      favoritesData: {},
      loaded: false,
    },
    mutations: {
      SET_FAVORITES_DATA: (state, favoritesData) => {
        state.favoritesData = favoritesData;
      },
      SET_LOADED: (state, loaded) => {
        state.loaded = loaded;
      },
    },
    actions: {
      loadFavoritesDataIfNeeded,
      loadFavoritesData,
    },
    getters: {},
  },
};

store.registerModule(exports.namespace.split('/'), exports.module);

export default exports;

async function loadFavoritesDataIfNeeded(
  { state, commit },
  payload = false
) {
  const { forceInline, mode, bootstrapContext } =
    normalizeBootstrapOptions(payload);

  if (state.loaded && !forceInline) {
    if (isBootstrapRequest(bootstrapContext)) {
      return Promise.resolve();
    }

    log.info('loadFavoritesDataIfNeeded: optimistic reload');
    store.dispatch(`${exports.namespace}/loadFavoritesData`);
    return Promise.resolve();
  }

  log.info('loadFavoritesDataIfNeeded: needed data');
  const language = store.state.i18n.language || 'en';

  return runSharedLoad(
    `${exports.namespace}:${language}`,
    () =>
      Promise.all([
        store.dispatch(`${exports.namespace}/loadFavoritesData`),
      ]).then(() => {
        commit('SET_LOADED', true);
      }),
    {
      bootstrapContext,
      metricKey: exports.namespace,
    }
  );
}

async function loadFavoritesData({ state, commit }) {
  const site = localeToCraftSiteHandle(store.state.i18n.language);

  const fetchFavoritesForSite = (siteCode) => {
    const query = `{
      entry(section: "favorites", site: "${siteCode}") {
        ... on favorites_favorites_Entry {
          favoritesHeader
          favoritesSubhead
        }
      }
    }`;
    return api.get(constants.GRAPH_QL_ENDPOINT, {
      params: { query: toGraphQLQuery(query) },
      headers: getGraphQLHeaders(),
    });
  };

  return fetchFavoritesForSite(site)
    .then((data) => {
      // If no data for this site, fall back to English
      if (!data.data.entry && site !== 'en') {
        log.info(`No favorites data for ${site}, falling back to English`);
        return fetchFavoritesForSite('en');
      }
      return data;
    })
    .then((data) => {
      log.info('Loaded favorites data', { data });
      commit('SET_FAVORITES_DATA', data.data.entry);
    })
    .catch((error) => {
      if (api.isCancel(error)) {
        log.info(`Caught cancellation of favorites request`);
        return;
      }
      log.error(`Error loading favorites data`, error, state);
      throw error;
    });
}
